/* One bar change at a time.
 *
 * Six paths read a token's bar, compute a new value and write it back: the
 * quick-apply row, the affect dialog, aura damage, effect damage, temporary hit
 * points, and the party sync. Nothing serialised them, so two sources landing
 * inside one Roll20 round trip both read 99, one wrote 89, the other wrote 91, and
 * a hit vanished — with both logged as applied, so nothing on screen said so.
 *
 * WHY A SINGLE QUEUE RATHER THAN ONE PER TOKEN.
 *
 * Per token, or per token and bar, would let unrelated creatures be hurt in
 * parallel and is the obviously "better" design. It is also harder to be sure
 * about: a batch write touches many tokens at once, so it would have to hold
 * several locks, and holding several locks is where deadlocks come from.
 *
 * Bar writes happen a few times a round and take a fraction of a second. The cost
 * of serialising them all is that the third of three simultaneous hits waits about
 * a second; the cost of getting the locking subtly wrong is a creature's hit points.
 * That trade is not close, and this can be made finer later with a test that proves
 * it.
 *
 * The job must do its READ inside, or the queue protects nothing — it exists to
 * make read-compute-write atomic, not to space out writes.
 */

/** The tail of the chain. Every job is appended to it. */
let chain: Promise<unknown> = Promise.resolve();

/* A job that never settles would block every later bar change for the rest of the
   session. The bridge has its own timeouts, but a bug here must not be able to
   wedge the queue: after this long the chain moves on regardless. The job itself
   is not cancelled — it simply stops being waited for. */
const STUCK_MS = 30_000;

const afterAtMost = (p: Promise<unknown>, ms: number) =>
  new Promise<void>((resolve) => {
    let done = false;
    const finish = () => { if (!done) { done = true; resolve(); } };
    const timer = setTimeout(finish, ms);
    void p.then(() => { clearTimeout(timer); finish(); }, () => { clearTimeout(timer); finish(); });
  });

/**
 * Run `job` once every earlier bar job has finished.
 *
 * The caller still gets its own result and its own errors: a failure here must not
 * be swallowed, because "not applied" has to reach the activity log. It also must
 * not break the chain for everybody behind it, which is why the chain waits on a
 * settled copy rather than on the job itself.
 */
export function withBarLock<T>(job: () => Promise<T>): Promise<T> {
  const mine = chain.then(job, job);
  chain = afterAtMost(mine, STUCK_MS);
  return mine;
}

/** Test seam: drop anything still queued. Never called by the app. */
export const resetBarQueue = () => { chain = Promise.resolve(); };
